import { axiosClient } from '@/libs/axios-client';

const API_BASE = '/client-reports';

// ============================================
// Client reports API (built from the AB Soft ledger)
// ============================================

/**
 * Pick the file name out of a Content-Disposition header, preferring the
 * UTF-8 form the server sends for names with Serbian letters.
 *
 * @param {string} disposition - Header value
 * @param {string} fallback - Name to use when none is given
 * @returns {string} File name
 */
const fileNameFrom = (disposition = '', fallback) => {
  const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) return decodeURIComponent(utf8[1]);
  const plain = disposition.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1] : fallback;
};

/**
 * Companies whose books arrive from AB Soft, with their latest delivery.
 *
 * @returns {Promise<Object>} { success, data: [{ _id, name, pib, lastSync }] }
 */
export const getCompanies = async () => {
  const response = await axiosClient.get(`${API_BASE}/companies`);
  return response.data;
};

/**
 * FCCS rows and checks for one month.
 *
 * @param {string} companyId - Company id
 * @param {Object} period - { year, month }
 * @returns {Promise<Object>} { success, data }
 */
export const getFccs = async (companyId, { year, month }) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/fccs`, {
    params: { year, month },
  });
  return response.data;
};

/**
 * FCCS upload as Excel.
 *
 * @param {string} companyId - Company id
 * @param {Object} period - { year, month }
 * @returns {Promise<Object>} { blob, fileName }
 */
export const exportFccs = async (companyId, { year, month }) => {
  const response = await axiosClient.get(
    `${API_BASE}/${companyId}/fccs/export`,
    { params: { year, month }, responseType: 'blob' }
  );
  return {
    blob: response.data,
    fileName: fileNameFrom(response.headers['content-disposition'], 'FCCS.xlsx'),
  };
};

/**
 * @param {string} companyId - Company id
 * @returns {Promise<Object>} { success, data: { partnerCodes, accountCodes, updatedAt } }
 */
export const getFccsConfig = async (companyId) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/fccs/config`);
  return response.data;
};

/**
 * @param {string} companyId - Company id
 * @param {Object} config - { partnerCodes, accountCodes }
 * @returns {Promise<Object>} { success, data }
 */
export const saveFccsConfig = async (companyId, config) => {
  const response = await axiosClient.put(
    `${API_BASE}/${companyId}/fccs/config`,
    config
  );
  return response.data;
};

/**
 * Learn the codes from an FCCS upload made by hand. Nothing is saved.
 *
 * @param {string} companyId - Company id
 * @param {File} file - The .xlsx that was sent
 * @returns {Promise<Object>} { success, data: { year, month, config, comparison, unresolved } }
 */
export const importFccsConfig = async (companyId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosClient.post(
    `${API_BASE}/${companyId}/fccs/config/import`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

/**
 * Accounts, partners and group codes for the code pickers.
 *
 * @param {string} companyId - Company id
 * @returns {Promise<Object>} { success, data: { accounts, partners, relatedParties } }
 */
export const getFccsReference = async (companyId) => {
  const response = await axiosClient.get(
    `${API_BASE}/${companyId}/fccs/reference`
  );
  return response.data;
};

// ============================================
// Cash flow (Telekom group form)
// ============================================

/**
 * Months 1..month: frozen months as sent to the group, the rest from the ledger.
 *
 * @param {string} companyId - Company id
 * @param {Object} period - { year, month }
 * @returns {Promise<Object>} { success, data: { year, month, rules, opening, months } }
 */
export const getCashFlow = async (companyId, { year, month }) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/cash-flow`, {
    params: { year, month },
  });
  return response.data;
};

/**
 * The cash flow as the group's Excel form.
 *
 * @param {string} companyId - Company id
 * @param {Object} period - { year, month }
 * @returns {Promise<Object>} { blob, fileName }
 */
export const exportCashFlow = async (companyId, { year, month }) => {
  const response = await axiosClient.get(
    `${API_BASE}/${companyId}/cash-flow/export`,
    { params: { year, month }, responseType: 'blob' }
  );
  return {
    blob: response.data,
    fileName: fileNameFrom(response.headers['content-disposition'], 'Cash flow.xlsx'),
  };
};

/**
 * @param {string} companyId - Company id
 * @returns {Promise<Object>} { success, data: rules, openingBalances, frozenMonths }
 */
export const getCashFlowConfig = async (companyId) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/cash-flow/config`);
  return response.data;
};

/**
 * @param {string} companyId - Company id
 * @param {Object} config - The sorting rules
 * @returns {Promise<Object>} { success, data }
 */
export const saveCashFlowConfig = async (companyId, config) => {
  const response = await axiosClient.put(
    `${API_BASE}/${companyId}/cash-flow/config`,
    config
  );
  return response.data;
};

/**
 * Compare a cash-flow file sent by hand with the ledger; with apply its months
 * are frozen as they are in the file.
 *
 * @param {string} companyId - Company id
 * @param {File} file - The .xlsx that was sent
 * @param {Object} options - { year, apply }
 * @returns {Promise<Object>} { success, data: { year, opening, months, unsupported, applied } }
 */
export const importCashFlow = async (companyId, file, { year, apply }) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosClient.post(
    `${API_BASE}/${companyId}/cash-flow/import`,
    formData,
    {
      params: { year, apply: apply ? 'true' : 'false' },
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
  return response.data;
};

/**
 * Let a frozen month follow the ledger again.
 *
 * @param {string} companyId - Company id
 * @param {Object} period - { year, month }
 * @returns {Promise<Object>} { success, data }
 */
export const unfreezeCashFlowMonth = async (companyId, { year, month }) => {
  const response = await axiosClient.delete(
    `${API_BASE}/${companyId}/cash-flow/frozen/${year}/${month}`
  );
  return response.data;
};

// ============================================
// Investments (Telekom group form)
// ============================================

/**
 * Months 1..month: frozen months as sent to the group, the rest from the
 * ledger, with the asset postings of the month.
 *
 * @param {string} companyId - Company id
 * @param {Object} period - { year, month }
 * @returns {Promise<Object>} { success, data: { year, month, template, rules, rows, months, lines } }
 */
export const getInvestments = async (companyId, { year, month }) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/investments`, {
    params: { year, month },
  });
  return response.data;
};

/**
 * The uploaded investments form filled up to the month.
 *
 * @param {string} companyId - Company id
 * @param {Object} period - { year, month }
 * @returns {Promise<Object>} { blob, fileName }
 */
export const exportInvestments = async (companyId, { year, month }) => {
  const response = await axiosClient.get(
    `${API_BASE}/${companyId}/investments/export`,
    { params: { year, month }, responseType: 'blob' }
  );
  return {
    blob: response.data,
    fileName: fileNameFrom(
      response.headers['content-disposition'],
      'Osnovna sredstva-investicije.xlsx'
    ),
  };
};

/**
 * @param {string} companyId - Company id
 * @returns {Promise<Object>} { success, data: { assetAccounts, excludedAccounts, mapping, rows, template, frozenMonths } }
 */
export const getInvestmentConfig = async (companyId) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/investments/config`);
  return response.data;
};

/**
 * @param {string} companyId - Company id
 * @param {Object} config - { assetAccounts, excludedAccounts, mapping }
 * @returns {Promise<Object>} { success, data }
 */
export const saveInvestmentConfig = async (companyId, config) => {
  const response = await axiosClient.put(
    `${API_BASE}/${companyId}/investments/config`,
    config
  );
  return response.data;
};

/**
 * Compare an investments file sent by hand with the ledger; with apply it
 * becomes the company's form and its months are frozen as they are in it.
 *
 * @param {string} companyId - Company id
 * @param {File} file - The .xlsx that was sent
 * @param {Object} options - { year, apply }
 * @returns {Promise<Object>} { success, data: { year, rows, months, unknownRows, applied } }
 */
export const importInvestments = async (companyId, file, { year, apply }) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosClient.post(
    `${API_BASE}/${companyId}/investments/import`,
    formData,
    {
      params: { year, apply: apply ? 'true' : 'false' },
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
  return response.data;
};

/**
 * Let a frozen month follow the ledger again.
 *
 * @param {string} companyId - Company id
 * @param {Object} period - { year, month }
 * @returns {Promise<Object>} { success, data }
 */
export const unfreezeInvestmentMonth = async (companyId, { year, month }) => {
  const response = await axiosClient.delete(
    `${API_BASE}/${companyId}/investments/frozen/${year}/${month}`
  );
  return response.data;
};

// ============================================
// Parent's income statement (e.g. Berkosan)
// ============================================

/**
 * Months 1..month: frozen months as sent to the parent, the rest from the
 * ledger, with the account + unit pairs that have no line.
 *
 * @param {string} companyId - Company id
 * @param {Object} period - { year, month }
 * @returns {Promise<Object>} { success, data: { year, month, template, rules, lines, months } }
 */
export const getParentIncomeStatement = async (companyId, { year, month }) => {
  const response = await axiosClient.get(
    `${API_BASE}/${companyId}/parent-income-statement`,
    { params: { year, month } }
  );
  return response.data;
};

/**
 * The uploaded income statement form filled up to the month.
 *
 * @param {string} companyId - Company id
 * @param {Object} period - { year, month }
 * @returns {Promise<Object>} { blob, fileName }
 */
export const exportParentIncomeStatement = async (companyId, { year, month }) => {
  const response = await axiosClient.get(
    `${API_BASE}/${companyId}/parent-income-statement/export`,
    { params: { year, month }, responseType: 'blob' }
  );
  return {
    blob: response.data,
    fileName: fileNameFrom(
      response.headers['content-disposition'],
      'INCOME STATEMENT.xlsx'
    ),
  };
};

/**
 * @param {string} companyId - Company id
 * @returns {Promise<Object>} { success, data: { mapping, lines, template, manualValues, frozenMonths } }
 */
export const getParentIncomeStatementConfig = async (companyId) => {
  const response = await axiosClient.get(
    `${API_BASE}/${companyId}/parent-income-statement/config`
  );
  return response.data;
};

/**
 * Save the map and/or the figures typed in for a month.
 *
 * @param {string} companyId - Company id
 * @param {Object} config - { mapping } or { manual: { year, month, values } }
 * @returns {Promise<Object>} { success, data }
 */
export const saveParentIncomeStatementConfig = async (companyId, config) => {
  const response = await axiosClient.put(
    `${API_BASE}/${companyId}/parent-income-statement/config`,
    config
  );
  return response.data;
};

/**
 * Compare an income statement file sent by hand with the ledger; with apply
 * it becomes the company's form, its map is taken over and its months are
 * frozen as they are in it.
 *
 * @param {string} companyId - Company id
 * @param {File} file - The .xlsx that was sent
 * @param {Object} options - { year, apply }
 * @returns {Promise<Object>} { success, data: { year, lines, mapping, months, unknownLines, applied } }
 */
export const importParentIncomeStatement = async (companyId, file, { year, apply }) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosClient.post(
    `${API_BASE}/${companyId}/parent-income-statement/import`,
    formData,
    {
      params: { year, apply: apply ? 'true' : 'false' },
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
  return response.data;
};

/**
 * Let a frozen month follow the ledger again.
 *
 * @param {string} companyId - Company id
 * @param {Object} period - { year, month }
 * @returns {Promise<Object>} { success, data }
 */
export const unfreezeParentIncomeStatementMonth = async (companyId, { year, month }) => {
  const response = await axiosClient.delete(
    `${API_BASE}/${companyId}/parent-income-statement/frozen/${year}/${month}`
  );
  return response.data;
};

// ============================================
// Tax receivable tracking table (customs VAT, e.g. Berkosan)
// ============================================

/**
 * The month's imports: VAT paid to customs (274 debit in UV orders).
 *
 * @param {string} companyId - Company id
 * @param {Object} period - { year, month }
 * @returns {Promise<Object>} { success, data: { year, month, rows, totals, sync } }
 */
export const getTaxReceivable = async (companyId, { year, month }) => {
  const response = await axiosClient.get(
    `${API_BASE}/${companyId}/tax-receivable`,
    { params: { year, month } }
  );
  return response.data;
};

/**
 * The month's table as the accountant's Excel.
 *
 * @param {string} companyId - Company id
 * @param {Object} period - { year, month }
 * @returns {Promise<Object>} { blob, fileName }
 */
export const exportTaxReceivable = async (companyId, { year, month }) => {
  const response = await axiosClient.get(
    `${API_BASE}/${companyId}/tax-receivable/export`,
    { params: { year, month }, responseType: 'blob' }
  );
  return {
    blob: response.data,
    fileName: fileNameFrom(
      response.headers['content-disposition'],
      'Tax Receivable Tracking Table.xlsx'
    ),
  };
};

// ============================================
// The nightly pull from the AB machine (platform operators)
// ============================================

/**
 * Every company's last pull, the trail of attempts, and the alert settings.
 *
 * @param {Object} [params] - { limit } how many attempts to return
 * @returns {Promise<Object>} { success, data: { companies, attempts, nightStart, settings } }
 */
export const getAbPulls = async (params) => {
  const response = await axiosClient.get(`${API_BASE}/ab/pulls`, { params });
  return response.data;
};

/**
 * @returns {Promise<Object>} { success, data: { enabled, recipients, checkHour, staleHours } }
 */
export const getAbAlerts = async () => {
  const response = await axiosClient.get(`${API_BASE}/ab/alerts`);
  return response.data;
};

/**
 * @param {Object} settings - { enabled, recipients, checkHour, staleHours }
 * @returns {Promise<Object>} { success, data }
 */
export const saveAbAlerts = async (settings) => {
  const response = await axiosClient.put(`${API_BASE}/ab/alerts`, settings);
  return response.data;
};

/**
 * Run the check now and send its mail, whatever the hour.
 *
 * @returns {Promise<Object>} { success, data: { sent, recipients, problems, okCount } }
 */
export const testAbAlert = async () => {
  const response = await axiosClient.post(`${API_BASE}/ab/alerts/test`);
  return response.data;
};

/**
 * The daily "approved on SEF vs booked in FIPO" table: its settings, with the
 * companies it can be sent for and the VAT month it covers today.
 *
 * @returns {Promise<Object>} { success, data }
 */
export const getErpBookingReport = async () => {
  const response = await axiosClient.get(`${API_BASE}/ab/booking-report`);
  return response.data;
};

/**
 * @param {Object} settings - { enabled, company, recipients, sendHour }
 * @returns {Promise<Object>} { success, data }
 */
export const saveErpBookingReport = async (settings) => {
  const response = await axiosClient.put(`${API_BASE}/ab/booking-report`, settings);
  return response.data;
};

/**
 * Build and send today's table now, whatever the hour.
 *
 * @returns {Promise<Object>} { success, data: { sent, recipients, period, rows, notBooked } }
 */
export const testErpBookingReport = async () => {
  const response = await axiosClient.post(`${API_BASE}/ab/booking-report/test`);
  return response.data;
};

// ============================================
// Input data as received from AB (read-only)
// ============================================

/**
 * @param {string} companyId - Company id
 * @returns {Promise<Object>} { success, data: [sync] } newest first
 */
export const getAbSyncs = async (companyId) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/ab/syncs`);
  return response.data;
};

/**
 * @param {string} companyId - Company id
 * @param {Object} params - year, month, account, partner, orderNo, text, page, pageSize
 * @returns {Promise<Object>} { success, data: { items, total, debit, credit } }
 */
export const getAbLines = async (companyId, params) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/ab/lines`, {
    params,
  });
  return response.data;
};

/**
 * @param {string} companyId - Company id
 * @param {Object} params - year, month, scope ('ytd' | 'month'), account
 * @returns {Promise<Object>} { success, data: { rows, debit, credit, balance } }
 */
export const getAbTrialBalance = async (companyId, params) => {
  const response = await axiosClient.get(
    `${API_BASE}/${companyId}/ab/trial-balance`,
    { params }
  );
  return response.data;
};

/**
 * Trial balance of an account range as AB's Excel.
 *
 * @param {string} companyId - Company id
 * @param {Object} params - year, month, scope, accountFrom, accountTo, analytic
 * @returns {Promise<Object>} { blob, fileName }
 */
export const exportAbTrialBalance = async (companyId, params) => {
  const response = await axiosClient.get(
    `${API_BASE}/${companyId}/ab/trial-balance/export`,
    { params, responseType: 'blob' }
  );
  return {
    blob: response.data,
    fileName: fileNameFrom(response.headers['content-disposition'], 'bruto bilans.xlsx'),
  };
};

/**
 * @param {string} companyId - Company id
 * @returns {Promise<Object>} { success, data: { accounts, partners } }
 */
export const getAbMasterData = async (companyId) => {
  const response = await axiosClient.get(
    `${API_BASE}/${companyId}/ab/master-data`
  );
  return response.data;
};

/**
 * AB opening balance prepared for BizniSoft: accounts, partners and banks
 * with BizniSoft codes, totals and warnings.
 *
 * @param {string} companyId - Company id
 * @param {Object} params - year, partnerStart, bankStart
 * @returns {Promise<Object>} { success, data }
 */
export const getBsMigration = async (companyId, params) => {
  const response = await axiosClient.get(
    `${API_BASE}/${companyId}/ab/bs-migration`,
    { params }
  );
  return response.data;
};

/**
 * The same as the Excel workbook to import into BizniSoft.
 *
 * @param {string} companyId - Company id
 * @param {Object} params - year, partnerStart, bankStart
 * @returns {Promise<Object>} { blob, fileName }
 */
export const exportBsMigration = async (companyId, params) => {
  const response = await axiosClient.get(
    `${API_BASE}/${companyId}/ab/bs-migration/export`,
    { params, responseType: 'blob' }
  );
  return {
    blob: response.data,
    fileName: fileNameFrom(response.headers['content-disposition'], 'BizniSoft prelazak.xlsx'),
  };
};

// ============================================
// The client's monthly pack (AB-format reports)
// ============================================

/**
 * @param {string} companyId - Company id
 * @returns {Promise<Object>} { success, data: { language, fxCurrency, reports } }
 */
export const getReportProfile = async (companyId) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/pack/profile`);
  return response.data;
};

/**
 * @param {string} companyId - Company id
 * @param {Object} profile - { language, fxCurrency, reports }
 * @returns {Promise<Object>} { success, data }
 */
export const saveReportProfile = async (companyId, profile) => {
  const response = await axiosClient.put(
    `${API_BASE}/${companyId}/pack/profile`,
    profile
  );
  return response.data;
};

/**
 * @param {string} companyId - Company id
 * @param {Object} params - year, month, fxRate
 * @returns {Promise<Object>} { success, data: { files, fx, fxError, sync } }
 */
export const getReportPack = async (companyId, params) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/pack`, {
    params,
  });
  return response.data;
};

/**
 * @param {string} companyId - Company id
 * @param {Object} params - year, month, title, fxRate
 * @returns {Promise<Object>} { blob, fileName }
 */
export const exportReportFile = async (companyId, params) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/pack/file`, {
    params,
    responseType: 'blob',
  });
  return {
    blob: response.data,
    fileName: fileNameFrom(response.headers['content-disposition'], 'report.xlsx'),
  };
};

/**
 * @param {string} companyId - Company id
 * @param {Object} params - year, month, fxRate
 * @returns {Promise<Object>} { blob, fileName }
 */
export const exportReportPackZip = async (companyId, params) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/pack/zip`, {
    params,
    responseType: 'blob',
  });
  return {
    blob: response.data,
    fileName: fileNameFrom(response.headers['content-disposition'], 'reports.zip'),
  };
};

// ============================================
// Releases: published to the client
// ============================================

/**
 * @param {string} companyId - Company id
 * @returns {Promise<Object>} { success, data: [release] } newest first
 */
export const getReleases = async (companyId) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/releases`);
  return response.data;
};

export const getFxCheck = async (companyId, { year, month }) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/fx-check`, {
    params: { year, month },
  });
  return response.data;
};

/**
 * HLB admins who can be copied on a publication.
 *
 * @param {string} companyId - Company id
 * @returns {Promise<Object>} { success, data: [{ email, name }] }
 */
export const getCopyCandidates = async (companyId) => {
  const response = await axiosClient.get(
    `${API_BASE}/${companyId}/releases/copy-candidates`
  );
  return response.data;
};

/**
 * The company's bookkeeper from the team assignments, copied automatically on
 * every publication.
 */
export const getReleaseBookkeepers = async (companyId) => {
  const response = await axiosClient.get(
    `${API_BASE}/${companyId}/releases/bookkeepers`
  );
  return response.data;
};

/**
 * Publish the month's pack to the client. The recipients chosen in the dialog
 * are saved on the pack and offered again next month.
 *
 * @param {string} companyId - Company id
 * @param {Object} payload - { year, month, fxRate, note, externalRecipients, copyRecipients, notifyClientUsers }
 * @returns {Promise<Object>} { success, data: release }
 */
export const publishRelease = async (companyId, payload) => {
  const response = await axiosClient.post(`${API_BASE}/${companyId}/releases`, payload);
  return response.data;
};

/**
 * @param {string} companyId - Company id
 * @param {string} releaseId - Release id
 * @returns {Promise<Object>} { success, data: release }
 */
export const withdrawRelease = async (companyId, releaseId) => {
  const response = await axiosClient.post(
    `${API_BASE}/${companyId}/releases/${releaseId}/withdraw`
  );
  return response.data;
};

/**
 * @param {string} companyId - Company id
 * @param {string} releaseId - Release id
 * @param {number} index - File index
 * @returns {Promise<Object>} { blob, fileName }
 */
export const downloadReleaseFile = async (companyId, releaseId, index) => {
  const response = await axiosClient.get(
    `${API_BASE}/${companyId}/releases/${releaseId}/files/${index}`,
    { responseType: 'blob' }
  );
  return {
    blob: response.data,
    fileName: fileNameFrom(response.headers['content-disposition'], 'report.xlsx'),
  };
};

/**
 * The client's published reports, every company they belong to.
 *
 * @returns {Promise<Object>} { success, data: [release] }
 */
export const getMyReleases = async () => {
  const response = await axiosClient.get(`${API_BASE}/my/releases`);
  return response.data;
};

/**
 * @param {string} releaseId - Release id
 * @param {number} index - File index
 * @returns {Promise<Object>} { blob, fileName }
 */
export const downloadMyReleaseFile = async (releaseId, index) => {
  const response = await axiosClient.get(
    `${API_BASE}/my/releases/${releaseId}/files/${index}`,
    { responseType: 'blob' }
  );
  return {
    blob: response.data,
    fileName: fileNameFrom(response.headers['content-disposition'], 'report.xlsx'),
  };
};

// ============================================
// Monthly result (APR income statement for the client)
// ============================================

/**
 * The monthly result: income statement for the month and the year to date,
 * markups and EBIT/EBITDA, for the accountant to check.
 *
 * @param {string} companyId - Company id
 * @param {Object} params - { year, month, language, currency, belowOperating, variant ('full' | 'short'), markup ('none' | 'mu1' | 'mu2' | 'both'), fxRate }
 * @returns {Promise<Object>} { success, data: { rows, indicators, indicatorRows, fx, notes, companySize, variant, markup } }
 */
export const getMonthlyResult = async (companyId, params) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/monthly-result`, {
    params,
  });
  return response.data;
};

/**
 * The monthly result as a file.
 *
 * @param {string} companyId - Company id
 * @param {Object} params - As getMonthlyResult, plus format ('xlsx' | 'pdf')
 * @returns {Promise<Object>} { blob, fileName }
 */
export const exportMonthlyResult = async (companyId, params) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/monthly-result/export`, {
    params,
    responseType: 'blob',
  });
  return {
    blob: response.data,
    fileName: fileNameFrom(
      response.headers['content-disposition'],
      params?.format === 'pdf' ? 'Mesecni rezultat.pdf' : 'Mesecni rezultat.xlsx'
    ),
  };
};

// ============================================
// IOS (izvod otvorenih stavki) — HLB accountants
// ============================================

/**
 * Dates with open items from AB, and dates asked for that have not come yet.
 *
 * @param {string} companyId - Company id
 * @returns {Promise<Object>} { success, data: { snapshots, pending } }
 */
export const getIosDates = async (companyId) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/ios/dates`);
  return response.data;
};

/**
 * Ask the AB machine for a date it does not send on its own.
 *
 * @param {string} companyId - Company id
 * @param {string} asOf - "YYYY-MM-DD"
 * @returns {Promise<Object>} { success, data: { asOf, status } }
 */
export const requestIosDate = async (companyId, asOf) => {
  const response = await axiosClient.post(`${API_BASE}/${companyId}/ios/requests`, { asOf });
  return response.data;
};

/**
 * Accounts of a date with partner and mismatch counts.
 *
 * @param {string} companyId - Company id
 * @param {string} asOf - "YYYY-MM-DD"
 * @returns {Promise<Object>} { success, data: { asOf, accounts } }
 */
export const getIosAccounts = async (companyId, asOf) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/ios/accounts`, {
    params: { asOf },
  });
  return response.data;
};

/**
 * Partners of one account on a date, with the check against the books.
 *
 * @param {string} companyId - Company id
 * @param {Object} params - { asOf, account }
 * @returns {Promise<Object>} { success, data }
 */
export const getIosOverview = async (companyId, params) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/ios`, { params });
  return response.data;
};

/**
 * One partner's IOS (PDF) or every partner's (ZIP).
 *
 * @param {string} companyId - Company id
 * @param {'pdf'|'zip'} kind - One partner or all
 * @param {Object} params - { asOf, account, partner?, language? }
 * @returns {Promise<Object>} { blob, fileName }
 */
export const exportIos = async (companyId, kind, params) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/ios/${kind}`, {
    params,
    responseType: 'blob',
  });
  return {
    blob: response.data,
    fileName: fileNameFrom(response.headers['content-disposition'], `IOS.${kind}`),
  };
};
